import { useState, useEffect } from "react";
import { FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { requestPasswordReset, resetPassword, verifyResetOtp } from "../../api/apiServices";
import { toast } from "react-toastify";

export default function ForgetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get email from previous screen state
  const email = location.state?.email || "";

  const [step, setStep] = useState(2); // Start at OTP step
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(192);

  useEffect(() => {
    if (step === 2 && timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  const formatTime = () => {
    const min = Math.floor(timer / 60);
    const sec = timer % 60;
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  /* STEP 2: VERIFY OTP */
  const handleCode = async () => {
    if (code.length < 4) {
      return setError("Enter a valid verification code");
    }

    try {
      setLoading(true);
      setError("");
      // Call verify endpoint
      await verifyResetOtp(email, code);
      setStep(3);
    } catch (err) {
      setError(err.message || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  /* STEP 3: RESET PASSWORD */
  const handlePassword = async () => {
    if (password.length < 6) {
      return setError("Password must be at least 6 characters");
    }
    if (password !== confirmPassword) {
      return setError("Passwords do not match");
    }

    try {
      setLoading(true);
      setError("");
      // Call reset endpoint
      await resetPassword(email, password);
      
      toast.success("Password reset successful!");
      navigate("/login");
    } catch (err) {
      setError(err.message || "Failed to reset password. Try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleResend = async () => {
  if (timer > 0) return; // Prevent resending if timer is still active

  try {
    setLoading(true);
    setError("");
    
    // Call your API service
    await requestPasswordReset(email);
    
    // Reset timer and notify user
    setTimer(192); 
    toast.success("A new code has been sent to your email.");
  } catch (err) {
    setError(err.message || "Failed to resend code. Please try again.");
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-white p-6 flex flex-col">
      <button onClick={() => navigate(-1)} className="mb-6 w-fit">
        <FiArrowLeft size={20} />
      </button>

      <div className="flex-1">
        {/* STEP 2: ENTER OTP */}
        {step === 2 && (
  <div className="flex flex-col">
    <h2 className="text-xl font-semibold mb-2">Check your Email</h2>
    <p className="text-gray-500 text-sm mb-6">
      We have sent a verification code to <span className="font-medium text-black">{email}</span>
    </p>

    <input
      placeholder="Enter 4-digit code"
      value={code}
      onChange={(e) => { setCode(e.target.value); setError(""); }}
      className={`w-full p-3 rounded-full border ${error ? "border-red-400" : "border-gray-300"} outline-none mb-2`}
    />

    {error && <p className="text-red-500 text-xs mb-2">{error}</p>}

    <div className="text-center">
      <button
        type="button"
        onClick={handleResend}
        disabled={timer > 0 || loading}
        className="text-sm font-medium disabled:text-gray-400 text-blue-600"
      >
        {timer > 0 ? `Resend code in ${formatTime()}` : "Resend Code"}
      </button>
    </div>

    <button
      onClick={handleCode}
      disabled={loading || code.length < 4}
      className="w-full mt-8 bg-[#2F3E6D] text-white py-3 rounded-full font-semibold disabled:bg-gray-400"
    >
      {loading ? "Verifying..." : "Verify OTP"}
    </button>
  </div>
)}

        {/* STEP 3: NEW PASSWORD */}
        {step === 3 && (
          <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}>
            <h2 className="text-xl font-semibold mb-6">Set a new password</h2>

            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                placeholder="Set new password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                className={`w-full p-3 rounded-full border ${error ? "border-red-400" : "border-gray-300"} outline-none`}
              />
              <button onClick={() => setShowPass(!showPass)} className="absolute right-4 top-4 text-gray-400">
                {showPass ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            <div className="relative mt-4">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                className={`w-full p-3 rounded-full border ${error ? "border-red-400" : "border-gray-300"} outline-none`}
              />
              <button onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-4 text-gray-400">
                {showConfirm ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            <button
              onClick={handlePassword}
              disabled={loading}
              className="w-full mt-10 bg-[#2F3E6D] text-white py-3 rounded-full font-semibold disabled:opacity-70"
            >
              {loading ? "Updating..." : "Done"}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}